import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { ClearanceRequestDocumentType } from "@/src/models/constants/document-types";
import { SCHEDULING_PRIORITY_PROMPT } from "@/src/models/constants/prompt";
import { Specialty } from "@/src/models/constants/specialty";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { SchedulingPriority } from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const clearanceRequestTypeDisplay: Record<ClearanceRequestDocumentType, string> = {
  [ClearanceRequestDocumentType.PRE_OP]: "Pre-Op",
  [ClearanceRequestDocumentType.SURGICAL]: "Surgical",
  [ClearanceRequestDocumentType.CLEARANCE_REQUEST]: "Clearance Request",
  [ClearanceRequestDocumentType.RESPONSE]: "Clearance Response",
  [ClearanceRequestDocumentType.RESPONSE_PRE_OP]: "Pre-Op Clearance Response",
  [ClearanceRequestDocumentType.RESPONSE_OTHER]: "Clearance Response (Other)",
};

export const clearanceRequestTypeSchema = z.nativeEnum(ClearanceRequestDocumentType);

const baseClearanceRequestFields = {
  patient: patientSchema,
  receivingLocation: locationSchema.nullable(),
};

const extendedClearanceRequestSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
  specialty: z.nativeEnum(Specialty).nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
  surgeryName: z.string().nullable(),
  clinicName: z.string().nullable(),
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
});

const clearanceRequestCustomFields: Record<ClearanceRequestDocumentType, Record<string, z.ZodTypeAny>> = {
  [ClearanceRequestDocumentType.PRE_OP]: extendedClearanceRequestSchema.pick({
    dateReceived: true,
    surgeryName: true,
    clinicName: true,
  }).shape,
  [ClearanceRequestDocumentType.SURGICAL]: extendedClearanceRequestSchema.pick({
    dateReceived: true,
    specialty: true,
  }).shape,
  [ClearanceRequestDocumentType.CLEARANCE_REQUEST]: extendedClearanceRequestSchema.shape,
  [ClearanceRequestDocumentType.RESPONSE]: extendedClearanceRequestSchema.shape,
  [ClearanceRequestDocumentType.RESPONSE_PRE_OP]: extendedClearanceRequestSchema.pick({
    dateReceived: true,
    clinicName: true,
    receivingProvider: true,
  }).shape,
  [ClearanceRequestDocumentType.RESPONSE_OTHER]: extendedClearanceRequestSchema.pick({
    dateReceived: true,
    clinicName: true,
    receivingProvider: true,
  }).shape,
};

const makeClearanceRequestSchema = (type: ClearanceRequestDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseClearanceRequestFields,
    ...customFields,
  });

export const clearanceRequestSchemas: Record<
  ClearanceRequestDocumentType,
  ReturnType<typeof makeClearanceRequestSchema>
> = (Object.values(ClearanceRequestDocumentType) as ClearanceRequestDocumentType[]).reduce(
  (acc, type) => {
    acc[type] = makeClearanceRequestSchema(type, clearanceRequestCustomFields[type]);

    return acc;
  },
  {} as Record<ClearanceRequestDocumentType, ReturnType<typeof makeClearanceRequestSchema>>
);

const clearanceRequestSchemasArray = (
  Object.values(ClearanceRequestDocumentType) as ClearanceRequestDocumentType[]
).map((type) => clearanceRequestSchemas[type]) as [
  ReturnType<typeof makeClearanceRequestSchema>,
  ...ReturnType<typeof makeClearanceRequestSchema>[],
];

export const anyClearanceRequestSchema = z.discriminatedUnion("type", clearanceRequestSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type ClearanceRequest = ExtractedDocumentFields<
  ClearanceRequestDocumentType,
  typeof extendedClearanceRequestSchema,
  typeof baseClearanceRequestFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const clearanceRequestShape: ClearanceRequest = {
  id: "",
  type: ClearanceRequestDocumentType.CLEARANCE_REQUEST,
  patient: null,
  receivingLocation: null,
  specialty: null,
  receivingProvider: null,
  dateReceived: null,
  dateSigned: null,
  schedulingPriority: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
